import { Dispatch, FC, Fragment, SyntheticEvent, forwardRef, useCallback, useState } from "react";
import { ActionType, AlertType } from "../../../../../../types/AlertType";
import { DataProfil } from "../../../../../../types/DataTypes";
import {
    Accordion, AccordionDetails, AccordionSummary, Box, Button, Card,
    CardContent, Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle, Divider, List, ListItem, ListItemText, Slide, Stack,
    TableCell, TableRow, Typography, styled, tableCellClasses
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { LoadingButton } from "@mui/lab";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import ProfilForm from "../../../../../forms/ProfilForm";
import { FeatureType } from "./Profil";
import Cookies from "universal-cookie";
import axios, { AxiosInstance } from "axios";
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loggoutUser } from "../../../../../../redux/actions/logginUser";
import CheckAttr from "../../../../../../utils/CheckAttr";
import OptionalWidthModal from "../../../../../../commons/optional-width-modal/OptionalWidthModal";
import { downloadManualUserFile } from "../../../../../../utils/utils";

interface CustomeBtnParam {
    bgColor: string;
    hoverColor: string;
    color?: string;
}

interface ProfilRowProps {
    profil: DataProfil;
    isDesktopOrLaptop: boolean;
    dataFeatures: FeatureType[];
    expanded: number | false;
    setExpanded: Dispatch<React.SetStateAction<number | false>>;
    handleRefreshProfilsDatas: (profil: DataProfil, action: ActionType) => void;
    setOpenSnack: Dispatch<React.SetStateAction<boolean>>;
    setAlerteMessage: Dispatch<React.SetStateAction<string>>;
    setAlertType: Dispatch<React.SetStateAction<AlertType>>;
}

const btnStyle = (colors: CustomeBtnParam) => {
    return {
        backgroundColor: colors?.bgColor,
        color: colors?.color ? colors?.color : "#fff",
        fontSize: 16,
        textTransform: 'none',
        '&:hover': {
            backgroundColor: colors?.hoverColor
        },
    }
}

const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.body}`]: {
        fontSize: 12,
        textAlign: 'center',
        color: "#fff"
    },
}));

const Transition = forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const urlBaseApi = process.env.REACT_APP_BASE_API_URL as string;

const ProfilRow: FC<ProfilRowProps> = ({
    profil,
    isDesktopOrLaptop,
    dataFeatures,
    expanded,
    setExpanded,
    handleRefreshProfilsDatas,
    setOpenSnack,
    setAlertType,
    setAlerteMessage
}) => {
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [openModalDelete, setOpenModalDelete] = useState<boolean>(false);

    const cookies = new Cookies();
    const token: string = cookies.get("jwt");

    const http: AxiosInstance = axios.create({
        baseURL: urlBaseApi,
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    const navigate = useNavigate();
    const dispatch = useDispatch();


    // Add global response interceptor
    http.interceptors.response.use(
        (response) => {
            // Modify response data here, if needed
            return response;
        },
        (error) => {
            console.log(error.message);
            if (error.message === "Request failed with status code 401") {
                // Remove the redux store
                dispatch(loggoutUser());

                // Remove token in navigator cookies
                cookies.remove('jwt', { path: '/' });

                navigate("/sign-in");
            }
            return Promise.reject(error);
        }
    );

    const handleClose = () => {
        setOpenModal(false);
    };

    const handleCloseDeleteModal = () => {
        setOpenModalDelete(false);
    };

    const featuresText = () => {
        const tabFeatures: string[] = [];
        for (let feature of profil?.features) {
            tabFeatures.push(feature?.libelle);
        }

        return tabFeatures.join(' / ');
    }

    const handleDelete = useCallback(() => {
        setDeleteLoading(true);
        http.delete(`profil/delete/${profil?.id}`).then(res => {
            setOpenModalDelete(false);
            setDeleteLoading(false);
            setAlerteMessage("Suppresion efféctuée !");
            setAlertType("success");
            setOpenSnack(true);
            handleRefreshProfilsDatas(profil, "DELETE");
        }).catch(error => {
            console.log(error);
            setAlertType("error");
            setOpenSnack(true);
        });
    }, []);

    const handleChangeAccordionValue = (panel: number) => (event: SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    const downloadManual = () => {
        if (profil?.manual_user) {
            downloadManualUserFile(profil?.manual_user);
        } else {
            setAlerteMessage("Il n'y a pas encore de manuel d'utilisateur parametré pour ce Profil.");
            setAlertType("warning");
            setOpenSnack(true);
        }
    };

    return (
        <>
            {isDesktopOrLaptop ? (
                <TableRow>
                    <StyledTableCell>{profil?.libelle}</StyledTableCell>
                    <StyledTableCell>{featuresText()}</StyledTableCell>
                    <StyledTableCell>
                        <Stack direction="row" spacing={1} display="flex" justifyContent="center">
                            <Button
                                size="small"
                                color="warning"
                                variant="contained"
                                sx={{ textTransform: 'none' }}
                                startIcon={<BorderColorIcon />}
                                onClick={() => setOpenModal(true)}
                            >
                                Modifier
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                color="inherit"
                                sx={{
                                    textTransform: 'none',
                                    bgcolor: '#fff',
                                    color: '#ff8f1f'
                                }}
                                startIcon={<DownloadIcon />}
                                onClick={() => downloadManual()}
                            >
                                Télécharger manuel
                            </Button>
                        </Stack>
                    </StyledTableCell>
                </TableRow>
            ) : (
                <Accordion
                    expanded={expanded === profil?.id}
                    onChange={handleChangeAccordionValue(profil?.id as number)}
                    sx={{
                        borderRadius: 1,
                        bgcolor: '#fff',
                        mb: 1,
                        // ml: 0.5
                    }}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: '#080d24' }} />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                    >
                        <List sx={{ py: 0 }} dense>
                            <ListItem sx={{ my: 0, py: 0 }} dense>
                                <ListItemText primary={profil?.libelle} sx={{ color: '#FC8404' }} />
                            </ListItem>
                        </List>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack direction='column' spacing={1}>
                            <Box mb={5}>
                                <Button
                                    variant="contained"
                                    sx={btnStyle({
                                        bgColor: "Gray",
                                        hoverColor: "Gray",
                                        color: "#fff",
                                    })}
                                    size='small'
                                    fullWidth
                                    startIcon={<BorderColorIcon />}
                                    onClick={() => setOpenModal(true)}
                                >
                                    Modifier
                                </Button >
                                <Button
                                    variant="contained"
                                    color="warning"
                                    fullWidth
                                    startIcon={<DownloadIcon />}
                                    sx={{
                                        textTransform: 'none',
                                        mt: 0.5
                                    }}
                                    onClick={() => downloadManual()}
                                >
                                    Télécharger manuel
                                </Button>
                            </Box>

                            {CheckAttr(profil?.features) && (
                                <>
                                    <Box mt={10}>
                                        <Typography
                                            gutterBottom
                                            variant="h6"
                                            component="div"
                                            sx={{ mb: 0 }}
                                        >
                                            Fonctionnalité
                                        </Typography>
                                        <Divider
                                            orientation="horizontal"
                                            sx={{ bgcolor: "#fff" }}
                                        />
                                    </Box>

                                    <Card>
                                        <CardContent>
                                            <List dense>
                                                {profil?.features?.map(feature => (
                                                    <Fragment key={feature?.id}>
                                                        <ListItem dense>
                                                            <ListItemText primary={
                                                                <Typography variant="caption">{feature?.libelle}</Typography>
                                                            } />
                                                        </ListItem>
                                                        <Divider
                                                            orientation="horizontal"
                                                            sx={{ bgcolor: " #dfdfdf" }}
                                                        />
                                                    </Fragment>
                                                ))}
                                            </List>
                                        </CardContent>
                                    </Card>
                                </>
                            )}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            )}

            <OptionalWidthModal
                title="Modifier un Profil"
                onClose={handleClose}
                open={openModal}
                breackPoint="md"
            >
                <ProfilForm
                    dataFeatures={dataFeatures}
                    profil={profil}
                    onClose={handleClose}
                    setOpenSnack={setOpenSnack}
                    setAlerteMessage={setAlerteMessage}
                    setAlertType={setAlertType}
                    handleRefreshProfilsDatas={handleRefreshProfilsDatas}
                />
            </OptionalWidthModal>

            <Dialog
                open={openModalDelete}
                TransitionComponent={Transition}
                keepMounted
                onClose={handleCloseDeleteModal}
                aria-describedby="alert-dialog-slide-description"
            >
                <DialogTitle>{"Confirmation"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-slide-description">
                        Vous êtes sur le point de supprimer ce profil,
                        êtes-vous sûr de vouloir poursuivre cette action ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <LoadingButton
                        onClick={() => handleDelete()}
                        endIcon={<DeleteForeverIcon />}
                        loading={deleteLoading}
                        loadingPosition="end"
                        sx={{ textTransform: "none" }}
                        variant="contained"
                        color="warning"
                    >
                        Oui
                    </LoadingButton>
                    <Button
                        onClick={handleCloseDeleteModal}
                        variant="contained"
                        color="inherit"
                        sx={{ textTransform: "none" }}
                        startIcon={<ExitToAppIcon />}
                    >
                        Non
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ProfilRow;